import {
  SimpleShowLayout,
  TextField,
  DateField,
  ReferenceField,
  BooleanField,
  FunctionField,
  Datagrid,
  useEditController,
  Labeled,
  ArrayField,
  SingleFieldList,
  ChipField,
  ShowContextProvider,
  useRedirect,
  RichTextField,
} from "react-admin";
import Divider from "@mui/material/Divider";
import CardContent from "@mui/material/CardContent";
import Card from "@mui/material/Card";
import CardMedia from "@mui/material/CardMedia";
import Grid from "@mui/material/Grid";
import CardHeader from "@mui/material/CardHeader";
import Stack from "@mui/material/Stack";
import { useMemo, useState, useEffect } from "react";
import moment from "moment";
import { HiEye } from "react-icons/hi";
import Typography from "@mui/material/Typography";
import { AppError } from "../../AppError";
import { useParams } from "react-router-dom";
import Button from "@mui/material/Button";
import { ActionIconButton } from "../../components/ActionIconButton";
import { AppLoading } from "../../components/AppLoading";
import { PageTopBreadcrumb } from "../../components/PageTopBreadcrumb";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import {
  assignCustomer,
  determineUserReadableRole,
  truncateString,
} from "./UserUtils";
import {
  createIuguCustomer,
  findIuguCustomer,
  isSingleEmail,
  iuguErrorMessage,
} from "./IuguUtils";
import { DateOnlyField } from "../../components/DateOnlyField";
import { displayDateOnly } from "../../util/dateOnly";
import { formatPhone } from "../../util/phone";
import { roleLabel } from "../../util/roleLabel";
import { formatCNPJ, formatCPF } from "./CPFUtils";
import { statusLabels } from "../../components/pages/iugu/iugu-utils";
import Avatar from "@mui/material/Avatar";
import { initials } from "../../util/text";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import { colors, radius, fontFamilyMono } from "../../data/tokens";
import { ParticipatedChip } from "../user-elections/user-elections-util";
import { roleStatusChipColor } from "../../components/RoleStatusChip";

// ------------------------------------------------------------------
// Cartões só aparecem quando há o que mostrar. Antes, blocos inteiros
// (Dependentes, Informações Comerciais, Divisões Técnicas) apareciam
// vazios, com título e cabeçalho de tabela sem nenhuma linha.
// ------------------------------------------------------------------

/** Ignora o `[{}]` que o banco usa como valor padrão de dependentes. */
const temDependentes = (record: any) =>
  Array.isArray(record?.dependents) &&
  record.dependents.some(
    (d: any) => d && Object.values(d).some((v) => v !== null && v !== "")
  );

// Tabelas dos cartões da ficha: cabeçalho no padrão das listas do
// painel (uppercase, cinza, fundo sutil) e tabela rente ao cartão —
// sem a moldura/elevação do Paper, que deixava o bloco "flutuando"
// com recuo lateral dentro do card.
const fichaTableSx = {
  "& .MuiTableCell-head": {
    fontSize: 11.5,
    fontWeight: 700,
    textTransform: "uppercase",
    letterSpacing: "0.04em",
    color: colors.textMuted,
    backgroundColor: colors.bgSubtle,
    border: 0,
    paddingTop: 1,
    paddingBottom: 1,
  },
  "& .MuiTableCell-body": {
    fontSize: 13,
    color: colors.textStrong,
  },
};

const CAMPOS_COMERCIAIS = [
  "commercialEmail",
  "commercialPhoneNumber",
  "commercialAddress",
  "commercialAddressNumber",
  "commercialAddressComplement",
  "commercialAddressNeighborhood",
  "commercialAddressZipCode",
  "commercialAddressCity",
  "commercialAddressState",
  "commercialAddressCountry",
];

const temInfoComercial = (record: any) =>
  !!record && CAMPOS_COMERCIAIS.some((campo) => record[campo]);

export const UserShow = (props: any) => {
  const redirect = useRedirect();
  const urlParams = useParams();
  const urlId = urlParams.id;

  const userRole = localStorage.getItem("roles");
  const auth = localStorage.getItem("auth");
  const authJson = auth ? JSON.parse(auth) : auth;
  const uid = authJson.id;
  if (auth && userRole) {
    // console.log("userId", uid);
    if (uid !== urlId && determineUserReadableRole(userRole) === "user") {
      redirect("/users/crud/" + uid + "/show");
    }
  } else {
    // redirect home
    redirect("/");
  }

  // console.log("props", props);
  // console.log("userAuth", userAuth);
  const [iuguSubscriptions, setIuguSubscriptions] = useState([]);
  const [transactions, setTransactions] = useState([]);
  const [transactionsSet, setTransactionsSet] = useState(false);
  const [foundCustomers, setFoundCustomers] = useState(true);
  const [assignMessage, setAssignMessage] = useState("");
  const [possibleClients, setPossibleClients] = useState<any[]>([]);
  const [createdClient, setCreatedClient] = useState({});
  const [iuguLoading, setIuguLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [appError, setAppError] = useState("");
  const autoLoadClient = localStorage.getItem("autoLoadClient");
  const roles = localStorage.getItem("roles");
  // console.log(useParams());

  const controller: any = useEditController({ resource: "users/crud", urlId });

  const createClient = async (data: any, id?: string) => {
    if (!data.email || !data.name || !data.cpf_cnpj) {
      setIuguLoading(false);
      return setAppError(
        "Email/CPF são obrigatórios para criação de contas IUGU. Atualize os dados cadastrais deste sócio."
      );
    }

    // Mais de um e-mail no campo (ou fora do formato): a IUGU recusaria
    if (!isSingleEmail(data.email)) {
      setIuguLoading(false);
      return setAppError(
        "O campo de e-mail da ficha tem mais de um endereço ou está fora do formato. Deixe um único e-mail na ficha do sócio e tente de novo."
      );
    }

    setAppError("");
    setAssignMessage("");

    let customer: any;
    try {
      customer = await createIuguCustomer(data);
    } catch (error: any) {
      setIuguLoading(false);
      // Some com o "Criar & Vincular": numa recusa, criar de novo dá o
      // mesmo erro até a ficha ser corrigida; numa falha de comunicação a
      // conta pode ter sido criada mesmo assim — só uma nova busca diz.
      setFoundCustomers(true);
      const reason = iuguErrorMessage(error);
      const message = reason.charAt(0).toUpperCase() + reason.slice(1);
      setAppError(
        error?.refused
          ? `${message}. Corrija a ficha do sócio e tente de novo — confira se o e-mail é um único endereço e se o CPF/CNPJ está correto.`
          : `${message}. Antes de tentar de novo, clique em "Iniciar Vinculação": a conta pode ter sido criada na IUGU mesmo assim.`
      );
      return;
    }

    setCreatedClient(customer);
    localStorage.setItem("autoLoadClient", customer.email);
    // A conta já existe na IUGU: daqui em diante só vincular, nunca criar
    setFoundCustomers(true);
    setPossibleClients([customer]);
    // Sucesso recarrega a página (o carregando segue até lá)
    const linked = id ? await assignCustomer(customer.id, id, true) : false;
    if (!linked) {
      setIuguLoading(false);
      setAppError(
        'A conta foi criada na IUGU, mas o vínculo com o sócio não foi gravado. Use "Vincular" na lista abaixo — não crie outra conta.'
      );
    }
  };

  /** Vincula uma conta IUGU já existente (lista de candidatos). */
  const linkExistingClient = async (iuguId: string, memberId: string) => {
    setIuguLoading(true);
    setAppError("");
    if (!(await assignCustomer(iuguId, memberId, true))) {
      setIuguLoading(false);
      setAppError(
        "Não foi possível gravar o vínculo. Esta conta da IUGU pode já estar vinculada a outro sócio — confira antes de tentar de novo."
      );
    }
  };

  /**
   * Procura conta existente na IUGU antes de deixar criar uma nova.
   * Busca por e-mail e também por CPF/CNPJ: muitos sócios foram cadastrados
   * na IUGU antes do sistema, às vezes com outro e-mail — buscar só por
   * e-mail fazia o operador criar conta duplicada.
   */
  const searchIuguClients = async (member: any) => {
    setAssignMessage("");
    try {
      const match = await findIuguCustomer({
        email: member.email,
        cpf: member.cpf,
        cnpj: member.cnpj,
      });

      setIuguLoading(false);
      setPossibleClients(match.candidates);

      if (match.customer) {
        setFoundCustomers(true);
        setAppError("");
        setAssignMessage(
          match.matchedBy === "documento"
            ? `Este sócio já possui conta na IUGU (encontrada pelo CPF/CNPJ, com o e-mail ${
                match.customer.email || "não informado"
              }). Use "Vincular" na lista abaixo — não crie uma nova conta.`
            : "Este sócio já possui conta na IUGU. Use \"Vincular\" na lista abaixo — não crie uma nova conta."
        );
        return;
      }

      if (match.candidates.length > 0) {
        // Achou parecidos, mas nenhum idêntico: decisão é do operador.
        setFoundCustomers(true);
        setAppError(
          "Nenhuma conta com e-mail ou CPF/CNPJ idênticos. Confira os cadastros parecidos abaixo antes de criar um novo."
        );
        return;
      }

      setFoundCustomers(false);
      setAppError(
        "Nenhum cliente encontrado por e-mail nem por CPF/CNPJ. Deseja criar este cliente na Iugu? (clique no botão Criar abaixo)"
      );
    } catch (error) {
      setIuguLoading(false);
      setFoundCustomers(true);
      setAppError(
        "Não foi possível consultar a IUGU agora. Tente novamente antes de criar uma conta nova."
      );
    }
  };

  // The URL has the following format: http://localhost:3001/#/users/crud/1/show where id is 1.
  // Function to get and set transactions based on the user id
  const getTransactions = async (id: string) => {
    // console.log("fetch get transactions", id);
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    // if (!authJson) return;
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/transactions/crud?filter={"memberId":"${id}"}&range=[0,9]&sort=["sCompetencia","DESC"]`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
      }
    );
    const data = await response.json();
    // console.log("fetch get transactions data", data, id);
    setTransactions(data);
    setTransactionsSet(true);
  };

  // Get the user id from the URL
  const url = window.location.href;
  const id = url.split("/")[6];
  // console.log("id", id);

  // Call the function to get the transactions
  if (!transactionsSet) getTransactions(id);

  const filters = useMemo(() => {
    if (transactions) {
      return {
        // mailName: searchText, // Use the 'q' filter for searching by email or name
      };
    }
    if (transactions) {
      const result = transactions;
      // console.log("RESULT", result);
      return result;
    }
    return {};
  }, [transactions]);
  const { record } = props;

  const fetchUserIuguSubscriptions = async (id: string) => {
    const auth = localStorage.getItem("auth");
    const authJson = auth ? JSON.parse(auth) : auth;
    const start = (page - 1) * perPage;
    const cidStr = `&customer_id=${id}`;
    const response = await fetch(
      `${process.env.REACT_APP_API_PATH}/iugu/subscriptions?s=${start}&l=${perPage}${cidStr}`,
      {
        headers: {
          "Content-Type": "application/json",
          Authorization: "Bearer " + authJson.access_token,
        },
      }
    );
    const data = await response.json();
    // console.log({ fetchUserIuguSubscriptions: data });
    setIuguSubscriptions(data.items);
  };
  // console.log("record", props);
  useEffect(() => {
    if (
      determineUserReadableRole(roles) === "admin" &&
      controller &&
      controller.record &&
      controller.record.iuguId
    ) {
      // console.log("mounting", controller.record.iuguId);
      fetchUserIuguSubscriptions(controller.record.iuguId);
    }
  }, [controller.record]);
  // useEffect(() => {
  //   if (createdClient && localStorage.getItem("autoLoadClient")) {
  //     getClientsByEmail(localStorage.getItem("autoLoadClient") || "");
  //   }
  // }, [createdClient]);

  return (
    <ShowContextProvider value={controller}>
      <SimpleShowLayout className="RaShowClass">
        <PageTopBreadcrumb
          title="Sócios"
          firstPath="/#/users/crud"
          secondTitle="Visualizando"
          thirdTitle={
            controller && controller.record && controller.record.fullName
          }
          actionType="edit"
        />
        <Grid container spacing={3}>
          <Grid item sm={6} xs={12}>
            <Card sx={{ padding: 2, marginBottom: 3 }}>
              <CardHeader title={"Informações Pessoais"} />
              <CardContent>
                <Grid container spacing={0.5}>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.fullName ? (
                            <Labeled>
                              <TextField source="fullName" label="Nome" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.email ? (
                            <Labeled>
                              <TextField source="email" label="Email" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      {/* <FunctionField
                        render={(record: any) =>
                          record.login ? (
                            <Labeled>
                              <TextField source="login" label="Login" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      /> */}
                      {/* <FunctionField
                        render={(record: any) =>
                          record.cpf ? (
                            <Labeled>
                              <TextField source="cpf" label="CPF" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      /> */}
                      <Labeled>
                        <BooleanField
                          source="hasPhysicalCard"
                          label="Possui Carteirinha"
                        />
                      </Labeled>
                      <Labeled>
                        <BooleanField source="userEnabled" label="Adimplência" />
                      </Labeled>
                      {/* <FunctionField
                        render={(record: any) =>
                          record.registrationNumber ? (
                            <Labeled>
                              <TextField
                                source="registrationNumber"
                                label="Matrícula"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      /> */}
                      <FunctionField
                        render={(record: any) =>
                          record.registrationNumberProv ? (
                            <Labeled>
                              <TextField
                                source="registrationNumberProv"
                                label="Matrícula (Provisória)"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.birthDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="birthDate"
                                locales="pt-BR"
                                label="Data de Nascimento"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.createdAt ? (
                            <Labeled>
                              <DateField
                                source="createdAt"
                                locales="pt-BR"
                                label="Criação da conta"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.gender ? (
                            <Labeled>
                              <FunctionField
                                label="Sexo"
                                render={(record: any) => (
                                  <>
                                    {record.gender === "F"
                                      ? "Feminino"
                                      : record.gender === "M"
                                      ? "Masculino"
                                      : "N/A"}
                                  </>
                                )}
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.maritalStatus ? (
                            <Labeled>
                              <FunctionField
                                label="Estado Civil"
                                render={(record: any) => (
                                  <>
                                    {record.maritalStatus === "SOLTEIRO"
                                      ? "Solteiro(a)"
                                      : record.maritalStatus === "CASADO"
                                      ? "Casado(a)"
                                      : record.maritalStatus === "DIVORCIADO"
                                      ? "Divorciado(a)"
                                      : record.maritalStatus === "VIUVO"
                                      ? "Viúvo(a)"
                                      : record.maritalStatus === "UNIAO_ESTAVEL"
                                      ? "União Estável"
                                      : record.maritalStatus === "DESQUITADO"
                                      ? "Desquitado(a)"
                                      : "N/A"}
                                  </>
                                )}
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.phoneNumber ? (
                            <Labeled>
                              <FunctionField
                                label="Celular"
                                render={(r: any) => formatPhone(r.phoneNumber)}
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.residencePhoneNumber ? (
                            <Labeled>
                              <FunctionField
                                label="Telefone Residencial"
                                render={(r: any) =>
                                  formatPhone(r.residencePhoneNumber)
                                }
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      {determineUserReadableRole(roles) === "admin" && (
                        <Labeled>
                          <ReferenceField
                            source="roleId"
                            reference="user-roles/crud"
                            link="show"
                            label="Regra de Acesso"
                          >
                            {/* tradução só de exibição (banco guarda
                                "User", "Admin"...) */}
                            <FunctionField
                              render={(r: any) => roleLabel(r?.name)}
                            />
                          </ReferenceField>
                        </Labeled>
                      )}
                    </Stack>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.CREA ? (
                            <Labeled>
                              <TextField source="CREA" label="CREA" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.professionalActivity ? (
                            <Labeled>
                              <TextField
                                source="professionalActivity"
                                label="Atividade Profissional Principal"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.accountStatus ? (
                            <Labeled>
                              <FunctionField
                                label="Situação da Conta"
                                render={(record: any) => (
                                  <>
                                    {record.accountStatus === "ACTIVE"
                                      ? "Ativa"
                                      : record.accountStatus === "DECEASED"
                                      ? "Falecido"
                                      : record.accountStatus === "CANCELED"
                                      ? "Cancelado"
                                      : record.accountStatus === "LICENSED"
                                      ? "Licenciado"
                                      : record.accountStatus === "PENDING"
                                      ? "Pendente"
                                      : record.accountStatus === "NO_CONTACT"
                                      ? "Sem Contato"
                                      : record.accountStatus === "UNKNOWN"
                                      ? "Desconhecido"
                                      : "N/A"}
                                  </>
                                )}
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.userCategoryId ? (
                            <Labeled>
                              <ReferenceField
                                source="userCategoryId"
                                reference="user-categories/crud"
                                link="show"
                                label="Categoria do Sócio"
                              >
                                <TextField source="name" />
                              </ReferenceField>
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.address ? (
                            <Labeled>
                              <TextField source="address" label="Endereço" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.addressNeighborhood ? (
                            <Labeled>
                              <TextField
                                source="addressNeighborhood"
                                label="Bairro"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.addressState ? (
                            <Labeled>
                              <TextField source="addressState" label="Estado" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.addressCity ? (
                            <Labeled>
                              <TextField source="addressCity" label="Cidade" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.addressZipCode ? (
                            <Labeled>
                              <TextField source="addressZipCode" label="CEP" />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.addressRegion ? (
                            <Labeled>
                              <TextField
                                source="addressRegion"
                                label="Região"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>

                {/* <FunctionField
                  label="Full Name"
                  render={(record: any) => (
                    <div className="flex flex-col space-y-2">
                      <div className="flex flex-col">
                        <b>{`${record.fullName}`}</b>
                      </div>
                      <div className="flex flex-col">
                        <Label className="mb-1">Matricula:</Label>
                        <b>{record.registrationNumber}</b>
                      </div>
                      <div className="flex flex-col">
                        <Label className="mb-1">Emails:</Label>
                        <b>
                          {record.email || "N/A"},{" "}
                          {record.commercialEmail || "N/A"}
                        </b>
                      </div>
                      <div className="flex flex-col">
                        <Label className="mb-1">Data de admissão:</Label>
                        <b>
                          <DateOnlyField source="admissionDate" locales={"pt-BR"} />
                        </b>
                      </div>
                    </div>
                  )}
                /> */}
              </CardContent>
            </Card>
            {/* O cartão aparece sempre; sem dependentes, mostra o aviso.
                Atenção ao `[{}]`: é o valor padrão do banco (312 sócios) e
                passava no `length > 0`, exibindo uma tabela sem linhas. */}
            {temDependentes(controller.record) && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardHeader title="Dependentes" />
                <CardContent>
                  <Grid item sm={12} xs={12}>
                    <FunctionField
                      render={(record: any) => (
                        <TableContainer>
                          <Table size="small" sx={fichaTableSx}>
                            <TableHead>
                              <TableRow>
                                <TableCell>Dependente</TableCell>
                                <TableCell align="right">Nascimento</TableCell>
                                <TableCell align="right">Parentesco</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {record.dependents &&
                                record.dependents.map((item: any, i: any) => {
                                  return (
                                    <TableRow
                                      key={i}
                                      sx={{
                                        "&:last-child td, &:last-child th": {
                                          border: 0,
                                        },
                                      }}
                                    >
                                      <TableCell
                                        component="th"
                                        scope="row"
                                        sx={{ fontWeight: 600 }}
                                      >
                                        {item.name}
                                      </TableCell>
                                      <TableCell align="right">
                                        {item.birthDate}
                                      </TableCell>
                                      <TableCell align="right">
                                        {item.kinship ? (
                                          <Chip
                                            size="small"
                                            variant="outlined"
                                            label={item.kinship}
                                          />
                                        ) : null}
                                      </TableCell>
                                    </TableRow>
                                  );
                                })}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      )}
                    />
                  </Grid>
                </CardContent>
              </Card>
            )}

            {controller.record?.userGraduations?.length > 0 && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardHeader title="Formações" />
                <CardContent>
                    <Grid container spacing={0.5}>
                      <Grid
                        item
                        sm={12}
                        xs={12}
                        sx={
                          {
                            // overflowX: "scroll",
                          }
                        }
                      >
                        <ArrayField label="Formações" source="userGraduations">
                          {/* Mesmo cabeçalho das tabelas da ficha; o
                              Paper que embrulhava dava moldura dupla */}
                          <Datagrid
                            bulkActionButtons={false}
                            size="small"
                            optimized
                            sx={{
                              boxShadow: "none",
                              "& .RaDatagrid-headerCell": {
                                fontSize: 11.5,
                                fontWeight: 700,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                                color: colors.textMuted,
                                backgroundColor: colors.bgSubtle,
                                border: 0,
                                paddingTop: 1,
                                paddingBottom: 1,
                              },
                              "& .RaDatagrid-rowCell": { fontSize: 13 },
                            }}
                          >
                            <TextField
                              source="graduation.name"
                              label="Graduação"
                              sx={{ fontWeight: 600 }}
                            />
                            <TextField
                              source="educationalInstitution.name"
                              label="Instituição de Ensino"
                            />
                            <TextField
                              source="yearCompletion"
                              label="Ano de Conclusão"
                              sx={{ fontFamily: fontFamilyMono }}
                            />
                          </Datagrid>
                        </ArrayField>
                      </Grid>
                    </Grid>
                </CardContent>
              </Card>
            )}
            {determineUserReadableRole(roles) === "admin" && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <FunctionField
                  render={(record: any) => (
                    <CardHeader
                      title="Integração IUGU"
                      subheader={
                        iuguLoading ? (
                          <AppLoading size={20} showText={true} />
                        ) : (
                          <></>
                        )
                      }
                    />
                  )}
                />

                <CardContent>
                  {appError && <AppError message={appError} title="Erro" />}
                  <FunctionField
                    label="Iugu ID"
                    render={(record: any) => {
                      if (record.iuguId) {
                        return (
                          <>
                            {/* Identificação da conta: rótulo discreto e o
                                código em monoespaçada, que é o que se copia */}
                            <Box sx={{ marginBottom: 2 }}>
                              <Typography
                                sx={{
                                  fontSize: 12,
                                  color: colors.textSecondary,
                                }}
                              >
                                Conta Iugu vinculada
                              </Typography>
                              <Typography
                                sx={{
                                  fontFamily: fontFamilyMono,
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: colors.textStrong,
                                  wordBreak: "break-all",
                                }}
                              >
                                {record.iuguId}
                              </Typography>
                            </Box>

                            {/* Antes eram dois botões idênticos, colados num
                                ButtonGroup. Emitir fatura é a ação do dia a
                                dia; assinatura é ocasional — daí a diferença
                                de peso e o espaço entre eles. */}
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                flexWrap: "wrap",
                                marginBottom: 2,
                              }}
                            >
                              <Button
                                variant="contained"
                                color="primary"
                                onClick={() => {
                                  window.location.href =
                                    "/#/iugu-invoices/create?uid=" + id;
                                }}
                              >
                                Criar fatura
                              </Button>
                              <Button
                                variant="outlined"
                                color="primary"
                                onClick={() => {
                                  window.location.href =
                                    "/#/subscriptions/create?uid=" + id;
                                }}
                              >
                                Criar assinatura
                              </Button>
                            </Box>

                            <Divider sx={{ marginBottom: 2 }} />

                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 700,
                                color: colors.textStrong,
                                marginBottom: 1,
                              }}
                            >
                              Assinaturas do Sócio
                            </Typography>

                            {iuguSubscriptions.length > 0 && (
                              <Table size="small">
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Plano</TableCell>
                                    <TableCell>Faturas</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {iuguSubscriptions.map(
                                    (subscription: any) => (
                                      <TableRow key={subscription.id}>
                                        <TableCell>
                                          <Button
                                            variant="outlined"
                                            color="primary"
                                            size="small"
                                            title={subscription.id}
                                            onClick={() => {
                                              // window.location.href =
                                              //   "/#/invoices/crud/" + invoice.id + "/show";
                                            }}
                                          >
                                            {truncateString(
                                              subscription.id,
                                              13
                                            )}
                                          </Button>
                                          <br />
                                          <Typography variant="caption">
                                            <b>Plano: </b>
                                            {subscription.plan_name ||
                                              "Desconhecido"}
                                            {/* <br />
                                        <b>Situação da Assinatura: </b>
                                        {subscription.active ? "Ativa" : "Não ativa"} */}
                                          </Typography>
                                        </TableCell>
                                        <TableCell>
                                          {subscription.recent_invoices.map(
                                            (item: any) => (
                                              <>
                                                <Table size="small">
                                                  <TableHead>
                                                    <TableRow>
                                                      <TableCell>
                                                        Cobrança
                                                      </TableCell>
                                                      <TableCell>
                                                        Vencimento
                                                      </TableCell>
                                                    </TableRow>
                                                  </TableHead>
                                                  <TableBody>
                                                    <TableRow key={item.id}>
                                                      <TableCell>
                                                        <Typography variant="caption">
                                                          {truncateString(
                                                            item.id || "",
                                                            13
                                                          )}
                                                          <br />
                                                          <b>
                                                            Status:{" "}
                                                            {statusLabels[
                                                              item.status
                                                            ] || item.status}
                                                          </b>
                                                        </Typography>
                                                      </TableCell>
                                                      <TableCell>
                                                        {moment(
                                                          item.due_date
                                                        ).format("DD-MM-YYYY")}
                                                      </TableCell>
                                                      <TableCell></TableCell>
                                                    </TableRow>
                                                  </TableBody>
                                                </Table>
                                                <Divider
                                                  sx={{ marginTop: 1 }}
                                                />
                                              </>
                                            )
                                          )}
                                        </TableCell>
                                      </TableRow>
                                    )
                                  )}
                                </TableBody>
                              </Table>
                            )}
                            <FunctionField
                              render={(record: any) => (
                                <>
                                  {iuguSubscriptions.length === 0 && (
                                    <Typography
                                      sx={{
                                        fontSize: 13,
                                        color: colors.textSecondary,
                                        marginBottom: 1,
                                      }}
                                    >
                                      Nenhuma assinatura ativa na Iugu.
                                    </Typography>
                                  )}
                                  {/* Ação secundária: não compete com os
                                      botões de criar, acima */}
                                  <Button
                                    variant="text"
                                    color="primary"
                                    size="small"
                                    sx={{ paddingX: 1.5 }}
                                    onClick={() => {
                                      window.location.href =
                                        "/#/subscriptions?customer_id=" +
                                        record.iuguId;
                                    }}
                                  >
                                    Ver todas as assinaturas
                                  </Button>
                                </>
                              )}
                            />
                          </>
                        );
                      } else {
                        return (
                          <>
                            {/* O aviso e as mensagens usavam classes
                                utilitárias de um tema antigo (p-4, mb-4…)
                                que não existem neste build — renderizava
                                tudo colado, sem fundo e sem respiro */}
                            <Typography
                              sx={{
                                fontSize: 13,
                                color: colors.textSecondary,
                                marginBottom: 2,
                              }}
                            >
                              Esta conta ainda não possui cliente vinculado na
                              Iugu, para emitir faturas ou criar assinatura,
                              faça a vinculação abaixo.
                            </Typography>
                            {assignMessage && (
                              <Typography
                                role="alert"
                                sx={{
                                  fontSize: 13,
                                  fontWeight: 600,
                                  color: colors.warning,
                                  marginBottom: 2,
                                }}
                              >
                                {assignMessage}
                              </Typography>
                            )}
                            <Box
                              sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}
                            >
                              {/* Desabilitados durante a operação: clique
                                  duplo no "Criar" criava duas contas */}
                              <Button
                                variant="contained"
                                disabled={iuguLoading}
                                onClick={() => {
                                  setIuguLoading(true);
                                  searchIuguClients(record);
                                }}
                              >
                                Iniciar Vinculação
                              </Button>
                              {!foundCustomers && (
                                <Button
                                  color="success"
                                  variant="contained"
                                  disabled={iuguLoading}
                                  onClick={() => {
                                    setIuguLoading(true);

                                    const data = {
                                      email: record.email,
                                      name: record.fullName,
                                      // Coletivos usam CNPJ; os demais, CPF
                                      cpf_cnpj: record.cnpj || record.cpf,
                                      // state: record.addressState,
                                      // city: record.addressCity,
                                      // zip_code: record.addressZipCode,
                                      // district: record.addressNeighborhood,
                                      // street: record.address,
                                    };
                                    // console.log("data", data);
                                    createClient(data, record.id);
                                  }}
                                >
                                  Criar & Vincular
                                </Button>
                              )}
                            </Box>
                            {/* A tabela de candidatos só existe depois da
                                busca — antes, o cabeçalho ID/Nome/Vincular
                                aparecia solto, sem linha nenhuma */}
                            {possibleClients.length > 0 && (
                              <>
                                <TableContainer sx={{ marginTop: 2 }}>
                                  <Table size="small" sx={fichaTableSx}>
                                    <TableHead>
                                      <TableRow>
                                        <TableCell>ID</TableCell>
                                        <TableCell>Nome</TableCell>
                                        <TableCell>Vincular</TableCell>
                                      </TableRow>
                                    </TableHead>
                                    <TableBody>
                                      {/* {JSON.stringify(possibleClients)} */}
                                      {possibleClients.map((client: any) => (
                                        <TableRow key={client.id}>
                                          <TableCell scope="row">
                                            <Typography fontSize={11}>
                                              {client.id}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            <Typography fontSize={11}>
                                              {client.name}
                                              <br />
                                              {formatCPF(client.cpf_cnpj)}
                                            </Typography>
                                          </TableCell>
                                          <TableCell>
                                            {/* Antes vinculava sem recarregar
                                                nem conferir o resultado: a
                                                tela não mudava, nem no
                                                sucesso nem na falha */}
                                            <Button
                                              variant="contained"
                                              size="small"
                                              color="primary"
                                              disabled={iuguLoading}
                                              onClick={() => {
                                                linkExistingClient(
                                                  client.id,
                                                  record.id
                                                );
                                              }}
                                            >
                                              Vincular
                                            </Button>
                                          </TableCell>
                                        </TableRow>
                                      ))}
                                    </TableBody>
                                  </Table>
                                </TableContainer>
                              </>
                            )}
                          </>
                        );
                      }
                    }}
                  />
                </CardContent>
              </Card>
            )}
          </Grid>
          <Grid item sm={6} xs={12}>
            <FunctionField
              render={(record: any) => (
                <Card sx={{ padding: 2, marginBottom: 3 }}>
                  <Grid container>
                    {/* Foto ou, na falta dela, as iniciais — mesmo avatar da
                        lista de Sócios. Antes caía numa imagem hospedada na
                        Wikipédia: dependência externa e visual destoante. */}
                    <Grid
                      item
                      xs={12}
                      sm={4}
                      lg={2}
                      sx={{ padding: 2, borderWidth: 1 }}
                    >
                      {record.physicalCardPicture &&
                      record.physicalCardPicture.path ? (
                        <CardMedia
                          component="img"
                          image={
                            `${process.env.REACT_APP_UPLOAD_PATH}/` +
                            record.physicalCardPicture.path
                          }
                          alt={record.fullName || "Foto do sócio"}
                          sx={{ borderRadius: `${radius.md}px` }}
                        />
                      ) : (
                        <Avatar
                          sx={{
                            width: 72,
                            height: 72,
                            fontSize: 26,
                            fontWeight: 700,
                            backgroundColor: colors.bgContent,
                            color: colors.primaryDark,
                          }}
                        >
                          {record.fullName ? initials(record.fullName) : null}
                        </Avatar>
                      )}
                    </Grid>
                    {/* Data */}
                    <Grid item xs={12} sm={8} lg={10} sx={{ padding: 2 }}>
                      <CardContent>
                        <Typography variant="h5">{record.fullName}</Typography>
                        {/* Rótulo só aparece com valor: coletivos migrados
                            ficam sem CPF e o "CPF:" vazio confundia */}
                        {(record.cpf || record.cnpj) && (
                          <Typography variant="body2">
                            {record.cpf && (
                              <>
                                CPF: {record.cpf}
                                <br />
                              </>
                            )}
                            {record.cnpj && (
                              <>
                                CNPJ: {formatCNPJ(record.cnpj)}
                                <br />
                              </>
                            )}
                          </Typography>
                        )}
                        {record.registrationNumber && (
                          <Typography variant="body2">
                            {/* Sem dígito não vira "41128-null" */}
                            Matrícula: {record.registrationNumber}
                            {record.registrationNumberDigit
                              ? `-${record.registrationNumberDigit}`
                              : ""}
                          </Typography>
                        )}
                        {/* Nascimento sai daqui: já aparece em "Informações
                            Pessoais" e em "Informações de Datas" */}

                        {
                          /** Observações: só para admin E só se houver texto —
                           *  antes o título aparecia sozinho em todo cadastro */
                          determineUserReadableRole(roles) === "admin" &&
                            record.observation &&
                            String(record.observation).replace(/<[^>]*>/g, "")
                              .trim() !== "" && (
                              <>
                                <hr />
                                <Typography variant="h6">
                                  Observações:
                                </Typography>
                                <RichTextField
                                  source="observation"
                                  label="Observações"
                                />
                              </>
                            )
                        }
                      </CardContent>
                    </Grid>
                  </Grid>
                </Card>
              )}
            />
            {controller.record?.positionRoles?.length > 0 && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardHeader title="Cargos e Funções" />
                <CardContent>
                    <Grid container spacing={0.5}>
                      <Grid item xs={12}>
                        <FunctionField
                          render={(record: any) => (
                            <TableContainer>
                              <Table size="small" sx={fichaTableSx}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Cargo</TableCell>
                                    <TableCell align="right">Status</TableCell>
                                    <TableCell align="right">Período</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {record.positionRoles &&
                                    record.positionRoles.map(
                                      (item: any, i: any) => {
                                        return (
                                          <TableRow
                                            key={i}
                                            sx={{
                                              "&:last-child td, &:last-child th":
                                                {
                                                  border: 0,
                                                },
                                            }}
                                          >
                                            <TableCell
                                              component="th"
                                              scope="row"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {item.name}
                                              {/* Complementos em linha
                                                  secundária discreta, sem
                                                  competir com o cargo */}
                                              {item.roleType ===
                                                "COMISSAO_EXTERNA" &&
                                                item.secondaryRole && (
                                                  <Typography
                                                    sx={{
                                                      fontSize: 12,
                                                      fontWeight: 400,
                                                      color: colors.textMuted,
                                                    }}
                                                  >
                                                    {item.secondaryRole}
                                                  </Typography>
                                                )}
                                              {item.technicalDivision &&
                                                item.technicalDivision.name && (
                                                  <Typography
                                                    sx={{
                                                      fontSize: 12,
                                                      fontWeight: 400,
                                                      color: colors.textMuted,
                                                    }}
                                                  >
                                                    Divisão Técnica:{" "}
                                                    {
                                                      item.technicalDivision
                                                        .name
                                                    }
                                                  </Typography>
                                                )}
                                            </TableCell>
                                            <TableCell align="right">
                                              {item.roleStatus &&
                                              item.roleStatus.name ? (
                                                <Chip
                                                  size="small"
                                                  variant="outlined"
                                                  color={roleStatusChipColor(
                                                    item.roleStatus.name
                                                  )}
                                                  label={item.roleStatus.name}
                                                />
                                              ) : null}
                                            </TableCell>
                                            <TableCell align="right">
                                              {/* Faixa de anos em destaque;
                                                  as datas exatas embaixo,
                                                  sem os prefixos "Período/
                                                  De/Até" que poluíam */}
                                              {item.roleType !==
                                                "COMISSAO_EXTERNA" &&
                                                item.yearRange && (
                                                  <Typography
                                                    sx={{
                                                      fontSize: 13,
                                                      fontWeight: 600,
                                                      fontFamily:
                                                        fontFamilyMono,
                                                      whiteSpace: "nowrap",
                                                    }}
                                                  >
                                                    {item.yearRange}
                                                  </Typography>
                                                )}
                                              {(item.fromDate ||
                                                item.toDate) && (
                                                <Typography
                                                  sx={{
                                                    fontSize: 12,
                                                    color: colors.textMuted,
                                                    whiteSpace: "nowrap",
                                                  }}
                                                >
                                                  {item.fromDate && item.toDate
                                                    ? `${displayDateOnly(
                                                        item.fromDate
                                                      )} – ${displayDateOnly(
                                                        item.toDate
                                                      )}`
                                                    : item.fromDate
                                                    ? `desde ${displayDateOnly(
                                                        item.fromDate
                                                      )}`
                                                    : `até ${displayDateOnly(
                                                        item.toDate
                                                      )}`}
                                                </Typography>
                                              )}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }
                                    )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        />
                      </Grid>
                    </Grid>
                </CardContent>
              </Card>
            )}

            {controller.record?.userElections?.length > 0 && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardHeader title="Eleições" />
                <CardContent>
                    <Grid container spacing={0.5}>
                      <Grid item xs={12}>
                        <FunctionField
                          render={(record: any) => (
                            <TableContainer>
                              <Table size="small" sx={fichaTableSx}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Eleição</TableCell>
                                    <TableCell align="right">
                                      Participou
                                    </TableCell>
                                    <TableCell align="right">Ano</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {record.userElections &&
                                    record.userElections.map(
                                      (item: any, i: any) => {
                                        return (
                                          <TableRow
                                            key={i}
                                            sx={{
                                              "&:last-child td, &:last-child th":
                                                {
                                                  border: 0,
                                                },
                                            }}
                                          >
                                            <TableCell
                                              component="th"
                                              scope="row"
                                              sx={{ fontWeight: 600 }}
                                            >
                                              {item.name}
                                            </TableCell>
                                            <TableCell align="right">
                                              {/* Badge global da tela de
                                                  Eleições (verde/cinza) */}
                                              <ParticipatedChip
                                                participated={
                                                  item.participated
                                                }
                                              />
                                            </TableCell>
                                            <TableCell
                                              align="right"
                                              sx={{
                                                fontFamily: fontFamilyMono,
                                              }}
                                            >
                                              {item.year}
                                            </TableCell>
                                          </TableRow>
                                        );
                                      }
                                    )}
                                </TableBody>
                              </Table>
                            </TableContainer>
                          )}
                        />
                      </Grid>
                    </Grid>
                </CardContent>
              </Card>
            )}

            <Card sx={{ padding: 2, marginBottom: 3 }}>
              <CardHeader title="Informações de Datas" />

              <CardContent>
                <Grid container spacing={0.5}>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.birthDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="birthDate"
                                locales="pt-BR"
                                label="Data de Nascimento"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.admissionDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="admissionDate"
                                locales="pt-BR"
                                label="Data de Admissão"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.remissionDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="remissionDate"
                                locales="pt-BR"
                                label="Data de Remido"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.aspirantDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="aspirantDate"
                                locales="pt-BR"
                                label="Data de Aspirante"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.collectiveDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="collectiveDate"
                                locales="pt-BR"
                                label="Data de Coletivo"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.seniorDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="seniorDate"
                                locales="pt-BR"
                                label="Data de Sênior"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.cancellationDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="cancellationDate"
                                locales="pt-BR"
                                label="Data de Cancelamento"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.deceasedDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="deceasedDate"
                                locales="pt-BR"
                                label="Data de Falecimento"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.licenseDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="licenseDate"
                                locales="pt-BR"
                                label="Data de Licenciado"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.effectiveDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="effectiveDate"
                                locales="pt-BR"
                                label="Data de Efetivo"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.collectiveRemissionDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="collectiveRemissionDate"
                                locales="pt-BR"
                                label="Data de Remido Coletivo"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.contributionDate ? (
                            <Labeled>
                              <DateOnlyField
                                source="contributionDate"
                                locales="pt-BR"
                                label="Data Contribuinte"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            {temInfoComercial(controller.record) && (
            <Card sx={{ padding: 2, marginBottom: 3 }}>
              <CardHeader title="Informações Comerciais" />
              <CardContent>
                <Grid container spacing={0.5}>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.commercialEmail ? (
                            <Labeled>
                              <TextField
                                source="commercialEmail"
                                label="Email Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.commercialAddress ? (
                            <Labeled>
                              <TextField
                                source="commercialAddress"
                                label="Endereço Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.commercialAddressNeighborhood ? (
                            <Labeled>
                              <TextField
                                source="commercialAddressNeighborhood"
                                label="Bairro Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.commercialAddressZipCode ? (
                            <Labeled>
                              <TextField
                                source="commercialAddressZipCode"
                                label="CEP Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                  <Grid item sm={6} xs={12}>
                    <Stack>
                      <FunctionField
                        render={(record: any) =>
                          record.commercialPhoneNumber ? (
                            <Labeled>
                              <FunctionField
                                label="Telefone Comercial"
                                render={(r: any) =>
                                  formatPhone(r.commercialPhoneNumber)
                                }
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.commercialAddressCity ? (
                            <Labeled>
                              <TextField
                                source="commercialAddressCity"
                                label="Cidade Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                      <FunctionField
                        render={(record: any) =>
                          record.commercialAddressState ? (
                            <Labeled>
                              <TextField
                                source="commercialAddressState"
                                label="Estado Comercial"
                              />
                            </Labeled>
                          ) : (
                            <></>
                          )
                        }
                      />
                    </Stack>
                  </Grid>
                </Grid>
              </CardContent>
            </Card>
            )}

            {controller.record?.technicalDivisions?.length > 0 && (
              <Card sx={{ padding: 2, marginBottom: 3 }}>
                <CardHeader title="Divisões Técnicas" />
                <CardContent>
                  <Typography variant="subtitle2">DTEs Atuais</Typography>
                  <ArrayField label="DTEs" source="technicalDivisions">
                    <SingleFieldList linkType={false}>
                      <ChipField source="name" size="small" />
                    </SingleFieldList>
                  </ArrayField>
                </CardContent>
              </Card>
            )}
            {(determineUserReadableRole(roles) === "admin" ||
              determineUserReadableRole(roles) === "finance" ||
              determineUserReadableRole(roles) === "secretary" ||
              determineUserReadableRole(roles) === "user") && (
              <Card
                sx={{
                  padding: 2,
                  marginBottom: 3,
                }}
              >
                <FunctionField
                  render={(record: any) => (
                    <CardHeader
                      title="Transações do Sócio"
                      // Atalho na linha do título — sem obrigar a rolar
                      // a tabela até o fim (padrão "Ver todas as
                      // assinaturas" do card Iugu; sócio comum não tem
                      // acesso à tela de Transações)
                      action={
                        determineUserReadableRole(roles) !== "user" ? (
                          <Button
                            variant="text"
                            color="primary"
                            size="small"
                            sx={{ paddingX: 1.5 }}
                            disabled={
                              transactions.length === 0 ||
                              determineUserReadableRole(roles) === "secretary"
                            }
                            onClick={() => {
                              window.location.href =
                                "/#/transactions/crud/?filter=%7B%22memberId%22%3A%22" +
                                record.id +
                                "%22%7D";
                            }}
                          >
                            Ver todas as transações
                          </Button>
                        ) : undefined
                      }
                    />
                  )}
                />

                <CardContent>
                  {/* Sem transações a tabela ficava só com o cabeçalho.
                      A condição espera a busca terminar (`transactionsSet`)
                      para não trocar o conteúdo durante o carregamento. */}
                  {transactionsSet && transactions.length === 0 && (
                    <Typography
                      sx={{ fontSize: 13, color: colors.textSecondary }}
                    >
                      Nenhuma transação registrada para este sócio.
                    </Typography>
                  )}
                  {(!transactionsSet || transactions.length > 0) && (
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <TableContainer>
                        <Table size="small" sx={fichaTableSx}>
                          <TableHead>
                            <TableRow>
                              <TableCell>Valor</TableCell>
                              <TableCell>Competência</TableCell>
                              <TableCell>Data</TableCell>
                              {/* Ações por último, à direita — padrão de
                                  todas as tabelas do painel */}
                              {determineUserReadableRole(roles) !== "user" && (
                                <TableCell align="right">Ações</TableCell>
                              )}
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {transactions &&
                              transactions.length > 0 &&
                              transactions.map((transaction: any) => (
                                <TableRow key={transaction.id}>
                                  <TableCell>
                                    {/* Mesmo desenho da lista de Transações:
                                        R$ pt-BR, mono, verde receita e
                                        vermelho despesa */}
                                    <Typography
                                      component="span"
                                      sx={{
                                        fontSize: 13,
                                        fontWeight: 600,
                                        fontFamily: fontFamilyMono,
                                        whiteSpace: "nowrap",
                                        color:
                                          transaction.type === "EXPENSE"
                                            ? colors.action
                                            : colors.success,
                                      }}
                                    >
                                      {Number(
                                        transaction.value ?? 0
                                      ).toLocaleString("pt-BR", {
                                        style: "currency",
                                        currency: "BRL",
                                      })}
                                    </Typography>
                                  </TableCell>
                                  <TableCell
                                    sx={{ fontFamily: fontFamilyMono }}
                                  >
                                    {transaction.sCompetencia || "--"}
                                  </TableCell>
                                  <TableCell>
                                    {moment(transaction.referenceDate).format(
                                      "DD-MM-YYYY"
                                    )}
                                  </TableCell>
                                  {determineUserReadableRole(roles) !==
                                    "user" && (
                                    <TableCell align="right">
                                      {/* Botão de ação global (36×36,
                                          simétrico) no lugar do botão
                                          preenchido largo */}
                                      <ActionIconButton
                                        title="Ver transação"
                                        icon={<HiEye />}
                                        disabled={
                                          determineUserReadableRole(roles) ===
                                          "secretary"
                                        }
                                        onClick={() => {
                                          window.location.href = `/#/transactions/crud/${transaction.id}/show`;
                                        }}
                                      />
                                    </TableCell>
                                  )}
                                </TableRow>
                              ))}
                          </TableBody>
                        </Table>
                      </TableContainer>
                      {/* </div> */}
                    </Grid>
                  </Grid>
                  )}
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </SimpleShowLayout>
    </ShowContextProvider>
  );
};
